import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Loader2,
  Download,
  Crown,
  Lock,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: "#94a3b8", font: { size: 12 } } },
  },
  scales: {
    x: { ticks: { color: "#64748b" }, grid: { color: "rgba(255,255,255,0.05)" } },
    y: { ticks: { color: "#64748b" }, grid: { color: "rgba(255,255,255,0.05)" } },
  },
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const isPro = user?.email === "ai.manifest.official@gmail.com" || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";

  useEffect(() => {
    fetchAnalytics();
    if (isPro) fetchGraphData();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchGraphData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/graph-data`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setGraphData(response.data);
    } catch {
      // PRO check handled by backend
    }
  };

  const exportAnalyticsPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/reports/analytics-pdf`, {
        responseType: "blob",
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `AURA_Analytics_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Analytics PDF downloaded!");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="analytics">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-aura-emerald" />
        </div>
      </DashboardLayout>
    );
  }

  const roi = analytics?.roi_percentage || 0;
  const isPositiveROI = roi > 0;

  // Prepare chart data
  const spendVsWinChart = graphData ? {
    labels: graphData.months.map(m => {
      const [y, mo] = m.split("-");
      return new Date(y, parseInt(mo) - 1).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
    }),
    datasets: [
      {
        label: "Spending ($)",
        data: graphData.spending_series,
        backgroundColor: "rgba(239, 68, 68, 0.6)",
        borderColor: "#ef4444",
        borderWidth: 1,
      },
      {
        label: "Winnings ($)",
        data: graphData.winnings_series,
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 1,
      },
    ],
  } : null;

  const roiChart = graphData ? {
    labels: graphData.months.map(m => {
      const [y, mo] = m.split("-");
      return new Date(y, parseInt(mo) - 1).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
    }),
    datasets: [{
      label: "ROI %",
      data: graphData.roi_series,
      borderColor: "#FFD700",
      backgroundColor: "rgba(255, 215, 0, 0.1)",
      fill: true,
      tension: 0.3,
      pointBackgroundColor: "#FFD700",
    }],
  } : null;

  const lotteries = graphData?.win_rate_by_lottery || {};
  const lotteryNames = Object.keys(lotteries);
  const winRateChart = lotteryNames.length > 0 ? {
    labels: lotteryNames.map(n => n.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())),
    datasets: [{
      data: lotteryNames.map(n => lotteries[n].win_rate),
      backgroundColor: ["#10b981", "#FFD700", "#8b5cf6", "#ef4444"],
      borderWidth: 0,
    }],
  } : null;

  return (
    <DashboardLayout currentPage="analytics">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-aura-text-secondary">Track your performance and insights</p>
          </div>
          <div className="flex gap-3">
            {isPro && (
              <Button
                className="btn-primary"
                onClick={exportAnalyticsPdf}
                disabled={exportingPdf}
                data-testid="export-analytics-pdf-btn"
              >
                {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-aura p-6" data-testid="stat-total-tickets">
            <div className="flex items-center justify-between mb-4">
              <span className="text-aura-text-muted text-sm">Total Games</span>
              <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-aura-emerald" />
              </div>
            </div>
            <p className="font-heading text-3xl font-bold text-white">{analytics?.total_tickets || 0}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-aura p-6" data-testid="stat-total-spend">
            <div className="flex items-center justify-between mb-4">
              <span className="text-aura-text-muted text-sm">Total Spend</span>
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <p className="font-heading text-3xl font-bold text-white">${(analytics?.total_spend || 0).toFixed(2)}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-aura p-6" data-testid="stat-total-winnings">
            <div className="flex items-center justify-between mb-4">
              <span className="text-aura-text-muted text-sm">Total Winnings</span>
              <div className="w-10 h-10 rounded-lg bg-aura-gold/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-aura-gold" />
              </div>
            </div>
            <p className="font-heading text-3xl font-bold text-aura-gold">${(analytics?.total_winnings || 0).toFixed(2)}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-aura p-6" data-testid="stat-roi">
            <div className="flex items-center justify-between mb-4">
              <span className="text-aura-text-muted text-sm">ROI</span>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPositiveROI ? "bg-green-500/10" : "bg-red-500/10"}`}>
                {isPositiveROI ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
              </div>
            </div>
            <p className={`font-heading text-3xl font-bold ${isPositiveROI ? "text-green-400" : "text-red-400"}`}>
              {roi.toFixed(2)}%
            </p>
          </motion.div>
        </div>

        {/* PRO Graphs Section */}
        {isPro && graphData ? (
          <div className="space-y-6 mb-8">
            {/* Spending vs Winnings */}
            {spendVsWinChart && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="card-aura p-6" data-testid="chart-spend-vs-win">
                <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-aura-emerald" />
                  Spending vs Winnings
                </h3>
                <div className="h-64">
                  <Bar data={spendVsWinChart} options={chartOptions} />
                </div>
              </motion.div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* ROI Trend */}
              {roiChart && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                  className="card-aura p-6" data-testid="chart-roi-trend">
                  <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-aura-gold" />
                    ROI Trend
                  </h3>
                  <div className="h-56">
                    <Line data={roiChart} options={chartOptions} />
                  </div>
                </motion.div>
              )}

              {/* Win Rate per Lottery */}
              {winRateChart && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                  className="card-aura p-6" data-testid="chart-win-rate">
                  <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-400" />
                    Win Rate by Lottery
                  </h3>
                  <div className="h-56 flex items-center justify-center">
                    <Doughnut
                      data={winRateChart}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: "bottom", labels: { color: "#94a3b8", padding: 16, font: { size: 11 } } },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.label}: ${ctx.raw}% win rate`
                            }
                          }
                        },
                      }}
                    />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Lottery Breakdown Table */}
            {lotteryNames.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                className="card-aura p-6" data-testid="lottery-breakdown-table">
                <h3 className="font-heading text-lg font-semibold text-white mb-4">Performance by Lottery</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-aura-text-muted font-medium">Lottery</th>
                        <th className="text-right py-3 px-4 text-aura-text-muted font-medium">Games</th>
                        <th className="text-right py-3 px-4 text-aura-text-muted font-medium">Wins</th>
                        <th className="text-right py-3 px-4 text-aura-text-muted font-medium">Spend</th>
                        <th className="text-right py-3 px-4 text-aura-text-muted font-medium">Winnings</th>
                        <th className="text-right py-3 px-4 text-aura-text-muted font-medium">Win Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotteryNames.map((name, i) => {
                        const s = lotteries[name];
                        return (
                          <tr key={name} className={`border-b border-white/5 ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}>
                            <td className="py-3 px-4 text-white capitalize">{name.replace(/_/g, " ")}</td>
                            <td className="py-3 px-4 text-right text-white">{s.tickets}</td>
                            <td className="py-3 px-4 text-right text-aura-emerald">{s.wins}</td>
                            <td className="py-3 px-4 text-right text-red-400">${s.spend.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right text-aura-gold">${s.winnings.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right text-white">{s.win_rate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        ) : !isPro ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="card-aura p-8 mb-8 border-aura-gold/30 bg-gradient-to-r from-aura-gold/5 to-transparent" data-testid="pro-graphs-cta">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-aura-gold/10 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-aura-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white">Unlock Analytics Graphs</h3>
                  <p className="text-aura-text-secondary text-sm mt-1">
                    Upgrade to AURA PRO to see spending vs winnings charts, ROI trends, and win rate breakdowns with PDF export.
                  </p>
                </div>
              </div>
              <Button className="btn-gold whitespace-nowrap" onClick={() => navigate("/subscription")} data-testid="upgrade-analytics-btn">
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to PRO
              </Button>
            </div>
          </motion.div>
        ) : null}

        {/* Insights */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="card-aura p-6" data-testid="best-strategy-card">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-aura-emerald" />
              Your AURA Mindset
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-aura-emerald/10 border border-aura-emerald/20">
                <p className="text-base font-medium text-aura-emerald leading-relaxed">
                  Stay positive, stay consistent.
                </p>
                <p className="text-sm text-aura-text-muted mt-3 leading-relaxed">
                  AURA is your data-driven companion for Australian lotteries. Combine discipline with optimism — track your games, review your patterns, and use your lucky numbers thoughtfully alongside AURA-generated combinations.
                </p>
              </div>
              <div className="p-3 rounded-lg bg-white/5">
                <p className="text-sm text-aura-text-secondary leading-relaxed">
                  Every draw is a fresh opportunity. AURA helps you track, analyse, and stay focused — your mindset keeps you in the game.
                </p>
              </div>
              <p className="text-xs text-aura-text-muted/60 italic">
                AURA is a lottery tracking and number analysis system. It does not guarantee wins. Always gamble responsibly.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="card-aura p-6" data-testid="frequent-numbers-card">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-aura-gold" />
              Your Most Played Numbers
            </h3>
            {analytics?.most_frequent_matches?.length > 0 ? (
              <div className="flex gap-2 flex-wrap">
                {analytics.most_frequent_matches.map((num, i) => (
                  <span key={i} className={`lottery-ball ${i === 0 ? "powerball" : ""}`}>{num}</span>
                ))}
              </div>
            ) : (
              <p className="text-aura-text-muted">Add more games to see your most played numbers</p>
            )}
          </motion.div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-xs text-aura-text-muted text-center leading-relaxed">
            AURA is a data-driven lottery tracking and number analysis system for Australian lotteries. It helps you track games, monitor spending, review results, and generate number combinations using historical analysis. Past performance does not indicate future results. Lottery outcomes are random. Always gamble responsibly. For help, call 1800 858 858 or visit gamblinghelponline.org.au.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
